import { REALM_TO_REGION } from '../../constants';
import { parseConfigValue, formatConfigValue } from './processorPaymentMethodList';

/** Unified row for the deployer table combining pending + history */
export interface DeployerRow {
    changeId: string;
    requestor: string;
    reviewer: string;
    createdAt: string;
    region: string;
    useCase: string;
    fromVersion: number;
    toVersion: number;
    status: 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'REJECTED';
    resolvedAt?: string;
    rejectionReason?: string;
    isPending: boolean;
    proposedConfig?: string;
}

/** A flattened row for the config table */
export interface ConfigRow {
    processor: string;
    paymentMethod: string;
    weblab: string;
    amountCap: string;
}

/** The raw config JSON shape from AppConfig */
export interface RawConfig {
    description?: string;
    processors: Record<string, { paymentMethods: Record<string, string> }>;
}

export const toAwsRegion = (realm: string): string => REALM_TO_REGION[realm] ?? realm;

/** Flatten the nested config JSON into rows */
export function configToRows(config: RawConfig): ConfigRow[] {
    const rows: ConfigRow[] = [];
    if (!config?.processors) return rows;
    for (const [processor, pData] of Object.entries(config.processors)) {
        for (const [pm, value] of Object.entries(pData.paymentMethods || {})) {
            const { weblab, amountCap } = parseConfigValue(value);
            rows.push({ processor, paymentMethod: pm, weblab, amountCap });
        }
    }
    rows.sort((a, b) => a.processor.localeCompare(b.processor) || a.paymentMethod.localeCompare(b.paymentMethod));
    return rows;
}

/** Convert rows back to config JSON (sorted alphabetically by processor then payment method) */
export function rowsToConfig(rows: ConfigRow[], description?: string): RawConfig {
    const sorted = [...rows].sort((a, b) => a.processor.localeCompare(b.processor) || a.paymentMethod.localeCompare(b.paymentMethod));
    const processors: RawConfig['processors'] = {};
    for (const row of sorted) {
        if (!processors[row.processor]) {
            processors[row.processor] = { paymentMethods: {} };
        }
        processors[row.processor].paymentMethods[row.paymentMethod] = formatConfigValue(row.weblab, row.amountCap);
    }
    return { description: description || '', processors };
}

/** Build a unique key for a processor + payment method combination */
export function rowKey(processor: string, paymentMethod: string): string {
    return `${processor}|${paymentMethod}`;
}
